import type { ReactNode } from 'react';

/**
 * Minimal inline icon set (1.5px stroke, per the design system) so the
 * admin UI has zero icon-library dependency.
 */

interface IconProps {
  size?: number;
}

/**
 * Google Material Symbols Outlined glyph (font loaded in index.html) — the
 * icon set the Stitch designs use. `fill` renders the filled variant.
 */
export function MaterialSymbol({
  name,
  size,
  fill,
}: {
  name: string;
  size?: number;
  fill?: boolean;
}): ReactNode {
  return (
    <span
      className="material-symbols-outlined"
      aria-hidden="true"
      style={{
        fontSize: size ?? 24,
        ...(fill ? { fontVariationSettings: "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" } : {}),
      }}
    >
      {name}
    </span>
  );
}

function base(size: number | undefined, children: ReactNode): ReactNode {
  return (
    <svg
      width={size ?? 18}
      height={size ?? 18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function IconDashboard({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </>,
  );
}

export function IconChat({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  );
}

export function IconSend({ size }: IconProps = {}): ReactNode {
  return (
    <svg
      width={size ?? 18}
      height={size ?? 18}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M3.4 20.4l17.45-7.48a1 1 0 0 0 0-1.84L3.4 3.6a.993.993 0 0 0-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.61c0 .71.73 1.2 1.39.91z" />
    </svg>
  );
}

export function IconBack({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </>,
  );
}

export function IconSearch({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </>,
  );
}

export function IconPin({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>,
  );
}

export function IconInsights({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <>
      <line x1="4" y1="20" x2="4" y2="12" />
      <line x1="10" y1="20" x2="10" y2="6" />
      <line x1="16" y1="20" x2="16" y2="10" />
      <line x1="22" y1="20" x2="22" y2="4" />
    </>,
  );
}

export function IconTrendUp({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </>,
  );
}

export function IconReply({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <>
      <polyline points="9 17 4 12 9 7" />
      <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </>,
  );
}

export function IconCheckCircle({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </>,
  );
}

export function IconClock({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15.5 14" />
    </>,
  );
}

/**
 * The storefront widget's own icon set (mirrors widget/src/widget.ts) so the
 * Storefront settings tab can preview exactly what customers will see.
 */
export function WidgetGlyph({ name, size }: { name: string; size?: number }): ReactNode {
  const s = size ?? 20;
  switch (name) {
    case 'bolt':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M11 21v-7H6l7-11v7h5l-7 11z" />
        </svg>
      );
    case 'help':
      return base(s, (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.1 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </>
      ));
    case 'message':
      return base(s, (
        <>
          <path d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8l-4 3.5V5a1 1 0 0 1 1-1z" />
          <line x1="8" y1="9" x2="16" y2="9" />
          <line x1="8" y1="13" x2="13" y2="13" />
        </>
      ));
    case 'send':
      return <IconSend size={s} />;
    case 'arrow':
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="12" y1="19" x2="12" y2="5" />
          <polyline points="5 12 12 5 19 12" />
        </svg>
      );
    case 'close':
      return base(s, (
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      ));
    case 'agent':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 3a7 7 0 0 0-7 7v1a2 2 0 0 0-1 1.73v2.54A2 2 0 0 0 6 17h1v-6H6a6 6 0 0 1 12 0h-1v6h-1.35A3.5 3.5 0 0 1 12.5 19H11a1 1 0 1 0 0 2h1.5a5.5 5.5 0 0 0 4.72-2.67A2 2 0 0 0 20 16.27v-2.54A2 2 0 0 0 19 12v-2a7 7 0 0 0-7-7z" />
        </svg>
      );
    case 'chat':
    default:
      return <IconChat size={s} />;
  }
}

export function IconInbox({ size }: IconProps = {}): ReactNode {
  return base(
    size,
    <>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>,
  );
}
